import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ponerPuerta } from '../lib/appliance';
import { alPerderSesion, ErrorDeApi, pedir } from '../lib/api';
import type { Usuario } from '../tipos';

interface Sesion {
  usuario: Usuario | null;
  cargando: boolean;
  ingresar: (correo: string, clave: string) => Promise<Usuario>;
  salir: () => Promise<void>;
}

const Contexto = createContext<Sesion | null>(null);
const CLAVE = ['/sesion'];

/**
 * Quién de Kuanta Bridge está en el Centro. La sesión es la cookie httpOnly que pone la
 * API: el JavaScript no la ve, así que para saber quién es se le pregunta a la API.
 */
export function SesionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const navegar = useNavigate();
  const donde = useLocation();

  const q = useQuery({
    queryKey: CLAVE,
    queryFn: async () => {
      try {
        const u = await pedir<Usuario & { puerta?: string }>('/sesion');
        // La API dice por dónde se entra a la app de un cliente; el panel ya no la hornea.
        ponerPuerta(u.puerta);
        return u;
      } catch (e) {
        if (e instanceof ErrorDeApi && e.codigo === 401) return null; // no hay sesión: al ingreso
        throw e;
      }
    },
    staleTime: Infinity,
    retry: (n, e) => !(e instanceof ErrorDeApi && e.codigo !== 0) && n < 3,
  });

  // Cualquier 401 en el camino (sesión vencida, clave cambiada en otro lado): al ingreso,
  // y después de entrar se vuelve adonde estaba.
  const perder = useCallback(() => {
    qc.setQueryData(CLAVE, null);
    qc.removeQueries({ predicate: (x) => x.queryKey[0] !== CLAVE[0] });
    if (donde.pathname !== '/ingresar') {
      const volver = donde.pathname + donde.search;
      navegar(`/ingresar?volver=${encodeURIComponent(volver)}&vencida=1`, { replace: true });
    }
  }, [qc, navegar, donde.pathname, donde.search]);
  useEffect(() => {
    alPerderSesion(perder);
    return () => alPerderSesion(null);
  }, [perder]);

  const valor = useMemo<Sesion>(
    () => ({
      usuario: q.data ?? null,
      cargando: q.isPending,
      ingresar: async (correo, clave) => {
        const u = await pedir<Usuario & { puerta?: string }>('/sesion', { metodo: 'POST', cuerpo: { correo, clave } });
        ponerPuerta(u.puerta);
        qc.setQueryData(CLAVE, u);
        return u;
      },
      salir: async () => {
        await pedir('/sesion', { metodo: 'DELETE' }).catch(() => undefined);
        qc.clear();
        qc.setQueryData(CLAVE, null);
        navegar('/ingresar', { replace: true });
      },
    }),
    [q.data, q.isPending, qc, navegar],
  );
  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useSesion(): Sesion {
  const s = useContext(Contexto);
  if (!s) throw new Error('useSesion fuera de SesionProvider');
  return s;
}
